import React from 'react';

import { analytics } from './analytics/analytics';
import { useCartApi } from './api/useCartApi';
import { useProductsApi } from './api/useProductsApi';
import { violetta } from './violettaSdk';

// Le card prodotto in chat hanno una CTA "aggiungi al carrello", ma il carrello è
// dello store: violetta chiede con l'evento "addToCart" e aspetta la risposta
// "addToCartResult". Qui si esegue la richiesta e si risponde.
//
// Il payload del confine porta il solo `skuId`, cioè la variante già scelta da
// violetta fra quelle disponibili. Non porta il prezzo, di proposito: è
// obbligatorio nel body di aggiunta, e accettarlo dall'iframe significherebbe
// inoltrare alle proprie api un valore non verificato. Va quindi risolto qui, e
// costa una chiamata in più. La quantità non c'è nel contratto ed è sempre 1.

// Registrazione una sola volta per caricamento di pagina, e non per istanza del
// componente: l'sdk accumula gli handler e non espone un "off", quindi ogni
// registrazione in più è un'aggiunta al carrello in più per ogni click. Un
// `useEffect` con dipendenze vuote NON basta: gli store girano in
// `React.StrictMode`, che in sviluppo esegue setup, cleanup e di nuovo setup, e
// qui un cleanup che disfi la registrazione non può esistere.
let registered = false;
// Per la stessa ragione l'handler registrato non può catturare `addSkus`/`getSku`
// dalla closure del primo mount: cambiano identità a ogni render, mentre la
// registrazione è una sola e per sempre. Il riferimento alla versione corrente sta
// nel modulo e non in una ref, e le due cose vanno insieme: `registered` misura la
// pagina, e se il riferimento misurasse l'istanza, dopo un remount l'handler già
// registrato continuerebbe a leggere una ref che nessuno scrive più — aggiunta al
// carrello morta in silenzio.
let currentHandler: ((skuId: string) => void) | undefined;

export const useViolettaAddToCart = (enable: boolean) => {
  const { addSkus } = useCartApi();
  const { getSku } = useProductsApi();

  const handleAddToCart = React.useCallback(
    async (skuId: string) => {
      // `ok` parte da false e la risposta a violetta è fuori dal try: nessun ramo,
      // nemmeno un errore imprevisto, può lasciare la card senza esito. Senza
      // risposta resta in attesa 10 secondi e poi si sblocca da sola, come se
      // l'integrazione non ci fosse.
      let ok = false;

      try {
        // `silent: false` per l'overlay di caricamento del sito, come per ogni
        // altra aggiunta al carrello; `suppressErrorModal` perché il fallimento lo
        // comunica già la card con "riprova", e un modal di piattaforma sarebbe un
        // secondo messaggio per lo stesso errore.
        const { data: sku } = await getSku(
          skuId,
          { populate: { price: true, product: true } },
          { silent: false, suppressErrorModal: true },
        );

        // Le tre guardie prima di qualsiasi altra chiamata: il budget è di 10
        // secondi, oltre i quali la card ha già rinunciato e una risposta tardiva
        // viene ignorata, lasciando il prodotto nel carrello senza che si veda.
        // `sku.price` in particolare non è pignoleria: `addSkus` senza prezzo
        // lancia in modo sincrono.
        if (sku.product === undefined) {
          // eslint-disable-next-line no-console
          console.warn(`[violetta] sku ${skuId} senza prodotto: aggiunta al carrello non tentata.`);
        } else if (sku.state !== 'AVAILABLE') {
          // eslint-disable-next-line no-console
          console.warn(`[violetta] sku ${skuId} non disponibile (${sku.state}): aggiunta al carrello non tentata.`);
        } else if (sku.price === undefined) {
          // eslint-disable-next-line no-console
          console.warn(`[violetta] sku ${skuId} senza prezzo: aggiunta al carrello non tentata.`);
        } else {
          const response = await addSkus([{ product: sku.product, quantity: 1, sku }], {
            suppressErrorModal: true,
          });

          // Da chiamare qui: `addSkus` non traccia da sé (a differenza di
          // `removeSku`). Senza, le aggiunte che arrivano dalla chat sparirebbero
          // dai dati, cioè proprio quelle che si vorrà misurare.
          analytics.events.addToCart(response.eventId, [{ partialSku: sku, quantity: 1 }]);
          ok = true;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(`[violetta] aggiunta al carrello dello sku ${skuId} non riuscita.`, error);
      }

      // Lo `skuId` va rimandato IDENTICO a quello ricevuto, senza normalizzarlo:
      // è la chiave con cui la card riconosce la risposta come propria. Per lo
      // stesso motivo non si usa `sku.id`, che qui sarebbe lo stesso valore ma
      // solo per coincidenza.
      violetta.command('send', 'addToCartResult', { ok, skuId });
    },
    [addSkus, getSku],
  );

  React.useEffect(() => {
    currentHandler = handleAddToCart;
  }, [handleAddToCart]);

  React.useEffect(() => {
    if (!enable || registered) {
      return;
    }

    registered = true;
    violetta.command('on', 'addToCart', ({ skuId }) => {
      currentHandler?.(skuId);
    });
  }, [enable]);
};
